import React, { useEffect, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { Tags, Plus, Trash2, Wand2 } from "lucide-react";

export default function ProductAliases() {
  const [aliases, setAliases] = useState([]);
  const [products, setProducts] = useState([]);
  const [alias, setAlias] = useState("");
  const [productId, setProductId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api("GET", "/product-aliases").then(setAliases).catch(() => {});
  useEffect(() => { load(); api("GET", "/products").then(setProducts).catch(() => {}); }, []);

  const add = async () => {
    if (!alias.trim() || !productId) { toast.error("Enter an alias and pick a product"); return; }
    try { await api("POST", "/product-aliases", { alias: alias.trim(), product_id: productId }); toast.success("Alias added"); setAlias(""); setProductId(""); load(); }
    catch (e) { toast.error(e.message); }
  };
  const del = async (id) => { try { await api("DELETE", `/product-aliases/${id}`); load(); } catch (e) { toast.error(e.message); } };
  const autoMap = async () => {
    setBusy(true);
    try {
      const a = await api("POST", "/aliases/auto-generate");
      const g = await api("POST", "/products/reclassify-groups");
      toast.success(`Auto-mapped ${a.created} recommendations across ${a.brands} brands · ${g.updated} products grouped`);
      load();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div data-testid="product-aliases" className="max-w-3xl">
      <div className="flex items-center gap-2 mb-1"><Tags className="h-6 w-6 text-sky-600" /><h1 className="font-display text-2xl font-bold text-slate-900">Product Aliases</h1></div>
      <p className="text-sm text-slate-500 mb-4">Map the chemical names your water-test recommends (e.g. "Pool Acid", "Hydrochloric Acid", "pH Down") to a real inventory product, so <b>Sell Recommended at POS</b> and <b>Buy Again</b> always find the right item.</p>

      <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 mb-4 flex items-center justify-between gap-3">
        <div className="text-[13px] text-sky-900">
          <b>Auto-map all brands</b> — matches every brand's chemistry to your dosing recommendations and refreshes the POS Chemicals / Spa / Pool groups. Safe to re-run any time you add products; your manual aliases below are never touched.
        </div>
        <button onClick={autoMap} disabled={busy} data-testid="alias-auto-map" className="shrink-0 rounded-lg bg-sky-600 text-white font-bold px-4 py-2.5 flex items-center gap-1.5 disabled:opacity-60"><Wand2 className="h-4 w-4" /> {busy ? "Mapping…" : "Auto-map all brands"}</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <div><label className="block text-[12px] font-bold text-slate-500 mb-1">Alias / recommended name</label>
            <input value={alias} onChange={(e) => setAlias(e.target.value)} data-testid="alias-name" placeholder='e.g. Pool Acid' className="w-full rounded border border-slate-300 px-3 py-2 text-sm" /></div>
          <div><label className="block text-[12px] font-bold text-slate-500 mb-1">Inventory product</label>
            <select value={productId} onChange={(e) => setProductId(e.target.value)} data-testid="alias-product" className="w-full rounded border border-slate-300 px-2 py-2 text-sm"><option value="">— pick a product —</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name}{p.brand ? ` (${p.brand})` : ""}</option>)}</select></div>
          <button onClick={add} data-testid="alias-add" className="rounded-lg bg-emerald-600 text-white font-bold px-4 py-2 flex items-center gap-1.5 h-[38px]"><Plus className="h-4 w-4" /> Add</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
        {aliases.length === 0 && <p className="text-sm text-slate-400 p-4 text-center">No aliases yet — tap Auto-map all brands to build them.</p>}
        {aliases.map((a) => (
          <div key={a.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`alias-row-${a.id}`}>
            <span className="font-semibold text-slate-800 whitespace-nowrap">“{a.alias}”</span>
            <span className="text-slate-400">→</span>
            <span className="flex-1 text-slate-600 truncate">{a.product_name}</span>
            {a.brand && <span className="rounded-full bg-slate-100 text-slate-600 text-[11px] font-bold px-2 py-0.5 whitespace-nowrap">{a.brand}</span>}
            {a.auto ? <span className="rounded-full bg-sky-100 text-sky-700 text-[11px] font-bold px-2 py-0.5">auto</span> : <span className="rounded-full bg-amber-100 text-amber-700 text-[11px] font-bold px-2 py-0.5">manual</span>}
            <button onClick={() => del(a.id)} data-testid={`alias-del-${a.id}`} className="text-rose-500 hover:text-rose-700"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
